import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getLanguageFromPath, getEnglishPath, getGermanPath } from "@/lib/language";

const BASE = "https://frostflotte.de";

/**
 * Sets a language-aware canonical plus de/en/x-default hreflang alternates
 * for every route. Written directly to <head> because this component is
 * mounted above the per-route HelmetProvider.
 */
export const HreflangTags = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isEnglish = getLanguageFromPath(pathname) === "en";
    const clean =
      pathname !== "/" && pathname !== "/en/" ? pathname.replace(/\/$/, "") : pathname;

    const dePath = isEnglish ? getGermanPath(clean) : clean;
    const enPath = isEnglish ? clean : getEnglishPath(clean);
    const deUrl = `${BASE}${dePath}`;
    const enUrl = `${BASE}${enPath}`;
    // Routen, die bewusst auf eine andere URL kanonisieren (Konsolidierung).
    const CANONICAL_OVERRIDES: Record<string, string> = {
      "/preise": "/",
      "/en/pricing": "/en/",
    };
    const override = CANONICAL_OVERRIDES[clean];
    const pageUrl = isEnglish ? enUrl : deUrl;
    const selfUrl = override ? `${BASE}${override}` : pageUrl;

    const upsert = (attrs: Record<string, string>, key: string, tag: "link" | "meta" = "link") => {
      let el = document.head.querySelector<HTMLElement>(`${tag}[data-lang-tag="${key}"]`);
      if (!el) {
        el = document.createElement(tag);
        el.setAttribute("data-lang-tag", key);
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    };

    // Runs repeatedly because page-level Helmet tags are flushed asynchronously.
    const apply = () => {
      document.head
        .querySelectorAll(
          'link[rel="canonical"]:not([data-lang-tag]), link[rel="alternate"][hreflang]:not([data-lang-tag]), meta[property="og:url"]:not([data-lang-tag]), meta[property="og:locale"]:not([data-lang-tag])',
        )
        .forEach((el) => el.parentNode?.removeChild(el));

      upsert({ rel: "canonical", href: selfUrl }, "canonical");
      upsert({ rel: "alternate", hreflang: "de", href: deUrl }, "alt-de");
      upsert({ rel: "alternate", hreflang: "en", href: enUrl }, "alt-en");
      upsert({ rel: "alternate", hreflang: "x-default", href: deUrl }, "alt-x");
      // og:url / og:locale sprachbewusst – Seiten setzen sonst hart die DE-Variante.
      upsert({ property: "og:url", content: pageUrl }, "og-url", "meta");
      upsert({ property: "og:locale", content: isEnglish ? "en_GB" : "de_DE" }, "og-locale", "meta");
      upsert(
        { property: "og:locale:alternate", content: isEnglish ? "de_DE" : "en_GB" },
        "og-locale-alt",
        "meta",
      );
      document.documentElement.setAttribute("lang", isEnglish ? "en" : "de");
    };

    const timers = [50, 400, 1200, 3000].map((d) => window.setTimeout(apply, d));

    return () => timers.forEach((t) => window.clearTimeout(t));


  }, [pathname]);

  return null;
};

export default HreflangTags;
